import { useState } from 'react';
import { toast } from 'sonner';
import { useChildRecommendations, useDeleteRecommendations } from '../hooks/useAdminRecommendations';
import type { ChildRecommendationGroup } from '../services/adminApi';

export default function AdminRecommendationsPage() {
  const [childIdInput, setChildIdInput] = useState('');
  const [activeChildId, setActiveChildId] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ChildRecommendationGroup | null>(null);

  const { data, isLoading, error, refetch } = useChildRecommendations(activeChildId);
  const deleteMutation = useDeleteRecommendations();

  const handleLookup = (e: React.FormEvent) => {
    e.preventDefault();
    const id = parseInt(childIdInput, 10);
    if (isNaN(id) || id <= 0) {
      toast.error('Please enter a valid child ID');
      return;
    }
    if (id === activeChildId) {
      refetch();
    } else {
      setActiveChildId(id);
    }
  };

  const handleDelete = async (deletionType: 'hard' | 'soft') => {
    if (!activeChildId || !deleteConfirm) return;

    try {
      const result = await deleteMutation.mutateAsync({
        childId: activeChildId,
        date: deleteConfirm.date,
        deletionType,
      });
      const modeLabel = deletionType === 'hard' ? 'Hard' : 'Soft';
      toast.success(`${modeLabel} deleted ${result.deletedCount} recommendations`);
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-heading-1 text-admin-slate-400">Recommendations</h1>

      {/* Lookup Form */}
      <form onSubmit={handleLookup} className="flex gap-3 items-end">
        <div>
          <label className="block text-sm text-admin-slate-500 mb-1">Child ID</label>
          <input
            type="number"
            value={childIdInput}
            onChange={(e) => setChildIdInput(e.target.value)}
            placeholder="Enter child ID"
            className="px-3 py-2 bg-admin-dark-700 border border-admin-slate-300/20 rounded text-admin-slate-300 w-48"
          />
        </div>
        <button
          type="submit"
          className="admin-button-primary px-4 py-2"
        >
          Look Up
        </button>
      </form>

      {isLoading && <p className="text-admin-slate-500">Loading...</p>}
      {error && <p className="text-red-400">{error instanceof Error ? error.message : 'Error'}</p>}

      {data && (
        <div className="space-y-4">
          <p className="text-admin-slate-400">
            Child: <span className="text-admin-slate-300 font-medium">{data.child.name}</span> (ID: {data.child.id})
          </p>

          {data.groups.length === 0 && (
            <p className="text-admin-slate-500">No recommendations found.</p>
          )}

          {data.groups.map((group) => (
            <div key={group.date} className="bg-admin-dark-800 rounded-lg border border-admin-slate-300/10 p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-admin-slate-300 font-medium">{group.date}</h3>
                  {group.job && (
                    <p className="text-xs text-admin-slate-500 mt-1">
                      Job: {group.job.id.slice(0, 8)}... | Status: {group.job.status} | Generated: {group.job.generatedCount}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setDeleteConfirm(group)}
                  disabled={deleteMutation.isPending || group.recommendations.every(r => r.isDeleted)}
                  className="px-3 py-1 text-xs rounded border border-red-500/30 text-red-400 hover:bg-red-900/20 disabled:opacity-50"
                >
                  Delete All
                </button>
              </div>

              <table className="w-full">
                <thead>
                  <tr className="border-b border-admin-slate-300/10">
                    <th className="px-3 py-2 text-left text-xs font-medium text-admin-slate-500 uppercase">ID</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-admin-slate-500 uppercase">Activity</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-admin-slate-500 uppercase">Activity ID</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-admin-slate-500 uppercase">Activity Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-admin-slate-500 uppercase">Created At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-admin-slate-300/10">
                  {group.recommendations.map((rec) => (
                    <tr key={rec.id} className={`hover:bg-admin-dark-700/50 ${rec.isDeleted ? 'opacity-50' : ''}`}>
                      <td className="px-3 py-2 text-admin-slate-400 text-sm">{rec.id}</td>
                      <td className={`px-3 py-2 text-sm ${rec.isDeleted ? 'line-through text-admin-slate-500' : 'text-admin-slate-300'}`}>
                        {rec.activityTitle}
                      </td>
                      <td className="px-3 py-2 text-admin-slate-400 text-sm">{rec.activityId}</td>
                      <td className="px-3 py-2 text-sm">
                        {rec.activityIsDeleted ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-900/30 text-red-400">Deleted</span>
                        ) : (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            rec.activityStatus === 'published'
                              ? 'bg-green-900/30 text-green-400'
                              : rec.activityStatus === 'archived'
                              ? 'bg-yellow-900/30 text-yellow-400'
                              : 'bg-gray-900/30 text-gray-400'
                          }`}>{rec.activityStatus}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-admin-slate-500 text-sm">
                        {rec.isDeleted && rec.deletedAt
                          ? `Deleted ${new Date(rec.deletedAt).toLocaleDateString()}`
                          : new Date(rec.createdAt).toLocaleTimeString()
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && data && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-admin-dark-800 border border-admin-slate-300/10 rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-medium text-admin-slate-300 mb-4">
              Delete Recommendations
            </h3>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-admin-slate-500">Child:</span>
                <span className="text-admin-slate-300">{data.child.name} (ID: {data.child.id})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-admin-slate-500">Date:</span>
                <span className="text-admin-slate-300">{deleteConfirm.date}</span>
              </div>
              <div>
                <span className="text-admin-slate-500">Activities ({deleteConfirm.recommendations.filter(r => !r.isDeleted).length}):</span>
                <ul className="mt-1 space-y-1 pl-4">
                  {deleteConfirm.recommendations.filter(r => !r.isDeleted).map((rec) => (
                    <li key={rec.id} className="text-admin-slate-300 list-disc">
                      {rec.activityTitle} <span className="text-admin-slate-500">(ID: {rec.activityId})</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <div className="rounded border border-red-500/20 bg-red-900/10 p-3">
                <p className="text-sm font-medium text-red-400">Hard Delete</p>
                <p className="text-xs text-admin-slate-500 mt-1">
                  Permanently removes records from the database. These activities <span className="text-admin-slate-300">will be eligible</span> for re-recommendation on next generation.
                </p>
              </div>
              <div className="rounded border border-yellow-500/20 bg-yellow-900/10 p-3">
                <p className="text-sm font-medium text-yellow-400">Soft Delete</p>
                <p className="text-xs text-admin-slate-500 mt-1">
                  Marks records as deleted. These activities <span className="text-admin-slate-300">will be excluded</span> from recommendations for ~7 days.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm rounded border border-admin-slate-300/20 text-admin-slate-400 hover:bg-admin-dark-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete('soft')}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm rounded bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Soft Delete'}
              </button>
              <button
                onClick={() => handleDelete('hard')}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Hard Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
